import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiErrorDetail } from "@/lib/api";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import SynapseLoader from "@/components/SynapseLoader";

/**
 * Pagina "Password dimenticata".
 * Chiede l'email → chiama POST /api/auth/forgot-password → mostra sempre la stessa
 * conferma neutra (per non rivelare quali email sono registrate).
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const API = process.env.REACT_APP_BACKEND_URL;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || "Errore durante l'invio.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020615] text-white flex items-center justify-center px-4 py-12"
         data-testid="forgot-password-page">
      <div className="max-w-md w-full">
        <div className="bg-white/5 border border-white/10 rounded-2xl backdrop-blur-md p-8 shadow-2xl">
          <div className="mb-6 text-center">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#10B981] mb-4">
              <Mail className="h-7 w-7 text-white" />
            </div>
            <h1 className="font-heading text-2xl font-extrabold tracking-tight">Password dimenticata?</h1>
            <p className="text-sm text-slate-300/80 mt-2">
              Inserisci la tua email: se è registrata su Synappsi ti invieremo un link per reimpostare la password.
            </p>
          </div>

          {!sent ? (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-slate-200 text-xs font-semibold">Email</Label>
                <Input id="email" type="email" value={email}
                       onChange={(e) => setEmail(e.target.value)}
                       placeholder="nome@dominio.it"
                       data-testid="forgot-email" required autoFocus
                       className="mt-1 h-11 bg-white/[0.08] border-white/15 text-white placeholder:text-slate-400
                                  focus:border-emerald-400 focus:ring-emerald-400/30" />
              </div>

              {error && (
                <div className="text-sm text-red-100 bg-red-500/20 border border-red-400/30 rounded-lg p-3"
                     data-testid="forgot-error">{error}</div>
              )}

              <Button type="submit" disabled={loading || !email}
                      data-testid="forgot-submit"
                      className="w-full h-12 rounded-xl text-base font-semibold
                                 bg-gradient-to-r from-[#1E3A8A] via-[#2563EB] to-[#10B981]
                                 hover:from-[#1E40AF] hover:via-[#3B82F6] hover:to-[#059669]
                                 text-white shadow-lg shadow-emerald-500/25 disabled:opacity-60">
                {loading ? (<><SynapseLoader size="sm" inline className="mr-2" /> Invio in corso…</>)
                        : "Invia link di reimpostazione"}
              </Button>
            </form>
          ) : (
            <div className="text-center py-4" data-testid="forgot-success">
              <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-emerald-500/20 mb-4">
                <CheckCircle2 className="h-8 w-8 text-emerald-400" />
              </div>
              <h2 className="font-heading text-lg font-bold text-white mb-2">Controlla la tua email</h2>
              <p className="text-sm text-slate-300/80">
                Se <strong className="text-emerald-300">{email}</strong> è registrato, ti abbiamo appena inviato
                un'email con il link per reimpostare la password.
              </p>
              <p className="text-xs text-slate-400 mt-4">
                Il link è valido per <strong>60 minuti</strong>. Se non ricevi l'email, controlla anche la cartella spam.
              </p>
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-white/10 text-center">
            <Link to="/login" className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-emerald-300"
                  data-testid="link-back-login">
              <ArrowLeft className="h-3.5 w-3.5" /> Torna al login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
